import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getCountryCallingCode, type Country } from 'react-phone-number-input';
import FlagIcon from '@/components/FlagIcon';
import { Popover, SearchField, cx } from '@/components/ui';

export interface CountrySelectOption {
    value?: string;
    label: string;
    divider?: boolean;
}

interface CountrySelectWithSearchProps {
    readonly name?: string;
    readonly value?: string;
    readonly onChange: (value: string | undefined) => void;
    readonly onFocus?: () => void;
    readonly onBlur?: () => void;
    readonly options: CountrySelectOption[];
    // iconComponent kept in props signature for library compatibility — replaced internally with SVG flags
    readonly iconComponent?: React.ComponentType<{ country?: string; label?: string; 'aria-hidden'?: boolean }>;
    readonly disabled?: boolean;
    readonly readOnly?: boolean;
    readonly tabIndex?: number;
    readonly className?: string;
    readonly 'aria-label'?: string;
}

const ZERO_WIDTH_SPACE = '​';
const UNFILTERED_LIMIT = 30;

/**
 * Country picker for react-phone-number-input: flag + dial code trigger, and a searchable
 * listbox in a Popover (renders inside the top open Dialog, so it works in the sign-in popup).
 * Keyboard: ↑/↓ move through the list, Enter picks, Escape closes.
 */
export function CountrySelectWithSearch({
    value,
    onChange,
    onFocus,
    onBlur,
    options,
    disabled = false,
    readOnly = false,
    tabIndex,
    className,
    ...rest
}: CountrySelectWithSearchProps) {
    const { t } = useTranslation();
    const listId = useId();
    const [search, setSearch] = useState('');
    const [active, setActive] = useState(0);
    const wasOpen = useRef(false);
    const listRef = useRef<HTMLUListElement>(null);

    const selectableOptions = useMemo(
        () => options.filter(o => !o.divider && (o.value || o.value === undefined)),
        [options],
    );

    const filteredOptions = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return selectableOptions.slice(0, UNFILTERED_LIMIT);
        return selectableOptions.filter(
            o =>
                o.label?.toLowerCase().includes(q) ||
                o.value?.toLowerCase().includes(q),
        );
    }, [selectableOptions, search]);

    const selectedOption = useMemo(
        () => selectableOptions.find(o => (o.value ?? 'ZZ') === (value ?? 'ZZ')),
        [selectableOptions, value],
    );

    const dialCode = useMemo(() => {
        if (!value) return null;
        try { return getCountryCallingCode(value as Country); } catch { return null; }
    }, [value]);

    // A new filter starts on the selected country when it is listed, else on the first match.
    useEffect(() => {
        const i = filteredOptions.findIndex(o => (o.value ?? 'ZZ') === (value ?? 'ZZ'));
        setActive(i >= 0 ? i : 0);
    }, [filteredOptions, value]);

    // Keep the active option visible inside the list (without scrolling the page).
    useEffect(() => {
        const list = listRef.current;
        const el = list?.children[active] as HTMLElement | undefined;
        if (!list || !el) return;
        if (el.offsetTop < list.scrollTop) list.scrollTop = el.offsetTop;
        else if (el.offsetTop + el.offsetHeight > list.scrollTop + list.clientHeight) {
            list.scrollTop = el.offsetTop + el.offsetHeight - list.clientHeight;
        }
    }, [active]);

    const isDisabled = disabled || readOnly;
    const countryLabel = rest['aria-label'] ?? 'Country';

    const handleSelect = (option: CountrySelectOption, close: () => void) => {
        if (option.divider) return;
        onChange(option.value === undefined || option.value === 'ZZ' ? undefined : option.value);
        close();
    };

    return (
        <div className="PhoneInputCountry relative">
            <Popover
                aria-label={countryLabel}
                align="start"
                width={300}
                onOpenChange={(open) => {
                    if (open) {
                        wasOpen.current = true;
                        return;
                    }
                    if (!wasOpen.current) return;
                    wasOpen.current = false;
                    setSearch('');
                    onBlur?.();
                }}
                trigger={(p) => (
                    <button
                        {...p}
                        type="button"
                        className={cx('PhoneInputCountrySelectTrigger', className)}
                        onClick={() => !isDisabled && p.onClick()}
                        onFocus={onFocus}
                        onBlur={() => !p['aria-expanded'] && onBlur?.()}
                        disabled={isDisabled}
                        tabIndex={tabIndex}
                        aria-label={countryLabel}
                    >
                        {selectedOption && (
                            <span className="PhoneInputCountryIcon shrink-0" aria-hidden>
                                <FlagIcon countryCode={value} size="md" />
                            </span>
                        )}
                        {dialCode && (
                            <span className="PhoneInputCountryDialCode tabular-nums leading-none text-primary" aria-hidden>
                                +{dialCode}
                            </span>
                        )}
                        <ChevronDown
                            className={cx('size-3 shrink-0 text-tertiary transition-transform', p['aria-expanded'] && 'rotate-180')}
                            aria-hidden
                        />
                    </button>
                )}
            >
                {(close) => (
                    <>
                        <div className="flex shrink-0 flex-col gap-1 border-b border-edge-subtle p-2">
                            <SearchField
                                value={search}
                                onChange={setSearch}
                                placeholder="Search country..."
                                aria-label={t('auth.countrySearch')}
                                clearLabel={t('common.clearSearch')}
                                size="sm"
                                role="combobox"
                                aria-expanded
                                aria-controls={listId}
                                aria-autocomplete="list"
                                aria-activedescendant={filteredOptions.length ? `${listId}-${active}` : undefined}
                                onKeyDown={(e) => {
                                    if (e.key === 'ArrowDown') {
                                        e.preventDefault();
                                        setActive(i => Math.min(i + 1, filteredOptions.length - 1));
                                    } else if (e.key === 'ArrowUp') {
                                        e.preventDefault();
                                        setActive(i => Math.max(i - 1, 0));
                                    } else if (e.key === 'Enter') {
                                        e.preventDefault();
                                        const option = filteredOptions[active];
                                        if (option) handleSelect(option, close);
                                    }
                                }}
                            />
                            {!search.trim() && (
                                <p className="m-0 px-1 font-ui text-small text-tertiary">
                                    Showing top {UNFILTERED_LIMIT} — type to search all countries
                                </p>
                            )}
                        </div>
                        {filteredOptions.length === 0 ? (
                            <p className="m-0 px-3 py-2 font-ui text-small text-tertiary">No country found</p>
                        ) : (
                            <ul
                                ref={listRef}
                                id={listId}
                                role="listbox"
                                aria-label={countryLabel}
                                className="relative m-0 max-h-56 min-h-0 list-none overflow-y-auto overscroll-contain p-1"
                            >
                                {filteredOptions.map((option, i) => {
                                    const selected = (option.value ?? 'ZZ') === (value ?? 'ZZ');
                                    return (
                                        <li
                                            key={option.value ?? 'ZZ'}
                                            id={`${listId}-${i}`}
                                            role="option"
                                            aria-selected={selected}
                                            onMouseEnter={() => setActive(i)}
                                            // Keep focus in the search field so the keyboard keeps working.
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => handleSelect(option, close)}
                                            className={cx(
                                                'flex cursor-pointer items-center gap-2 rounded-control px-3 py-2 font-ui text-body text-primary',
                                                i === active && 'bg-selected'
                                            )}
                                        >
                                            <FlagIcon countryCode={option.value} size="md" />
                                            <span className="min-w-0 truncate">{option.label || ZERO_WIDTH_SPACE}</span>
                                            {selected ? <Check aria-hidden className="ml-auto size-4 shrink-0 text-link" /> : null}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </>
                )}
            </Popover>
        </div>
    );
}
